/**
 * WebGL2 shader sources and compile/link helpers for the metro map renderer.
 *
 * Port 1-to-1 of:
 *   - metro-android `feature/metro-map/.../render/metro_vertex.glsl`
 *   - metro-android `feature/metro-map/.../render/metro_fragment.glsl`
 *
 * Vertex layout matches `FLOATS_PER_VERTEX = 6` from `../geometry/types.ts`:
 * interleaved `[x, y, r, g, b, a]` → attribute locations 0 (vec2 position)
 * and 1 (vec4 color). The view matrix is a column-major 4x4 mat4 uniform
 * produced by `./viewMatrix.ts`.
 */

export const VERTEX_SHADER_SOURCE = `#version 300 es
layout(location = 0) in vec2 aPosition;
layout(location = 1) in vec4 aColor;

uniform mat4 uViewMatrix;

out vec4 vColor;

void main() {
  gl_Position = uViewMatrix * vec4(aPosition, 0.0, 1.0);
  vColor = aColor;
}
`

export const FRAGMENT_SHADER_SOURCE = `#version 300 es
precision mediump float;

in vec4 vColor;
out vec4 fragColor;

void main() {
  fragColor = vColor;
}
`

/**
 * Compile a single shader stage. Throws with the driver info log on failure
 * so the caller sees a meaningful message rather than an opaque `null` return.
 */
export function compileShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
): WebGLShader {
  const shader = gl.createShader(type)
  if (shader === null) {
    throw new Error('gl.createShader returned null')
  }
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? '(no info log)'
    const stage = type === gl.VERTEX_SHADER ? 'vertex' : 'fragment'
    gl.deleteShader(shader)
    throw new Error(`Failed to compile ${stage} shader: ${log}`)
  }
  return shader
}

/**
 * Link a vertex + fragment shader pair into a program. Attached shaders are
 * deleted after linking — the program keeps its own reference.
 */
export function linkProgram(
  gl: WebGL2RenderingContext,
  vs: WebGLShader,
  fs: WebGLShader,
): WebGLProgram {
  const program = gl.createProgram()
  if (program === null) {
    throw new Error('gl.createProgram returned null')
  }
  gl.attachShader(program, vs)
  gl.attachShader(program, fs)
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) ?? '(no info log)'
    gl.deleteProgram(program)
    throw new Error(`Failed to link program: ${log}`)
  }
  gl.detachShader(program, vs)
  gl.detachShader(program, fs)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  return program
}

export const DIM_VERTEX_SHADER_SOURCE = `#version 300 es
layout(location = 0) in vec2 aPosition;

void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`

export const DIM_FRAGMENT_SHADER_SOURCE = `#version 300 es
precision mediump float;

uniform vec4 uDimColor;
uniform float uDimAlpha;

out vec4 fragColor;

void main() {
  fragColor = vec4(uDimColor.rgb, uDimColor.a * uDimAlpha);
}
`
